import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { MapPin, Star, Users, MessageSquare, Zap, Shield, ShieldOff, Clock, ArrowLeft, CalendarPlus, X, Flag, Bookmark, BookmarkCheck, RefreshCw } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { useFocusTrap } from "@/hooks/useFocusTrap";
import ReportIssueModal from "@/components/ReportIssueModal";
import ReliabilityBadge, { VerifiedPartnerBadge } from "@/components/ReliabilityBadge";
import SkillConfidenceBadge from "@/components/SkillConfidenceBadge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function SkillBadge({ level }) {
  const cls = { beginner: "badge-beginner", intermediate: "badge-intermediate", advanced: "badge-advanced", expert: "badge-expert" };
  return <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${cls[level] || "badge-intermediate"}`}>{level}</span>;
}

const SPORT_LABELS = {
  "tennis": "Tennis", "pickleball": "Pickleball", "golf": "Golf", "table-tennis": "Table Tennis",
  "ski": "Skiing", "padel": "Padel", "squash": "Squash", "badminton": "Badminton", "climbing": "Climbing"
};

const SPORTS_LIST = [
  { value: "tennis", label: "Tennis" }, { value: "pickleball", label: "Pickleball" },
  { value: "golf", label: "Golf" }, { value: "table-tennis", label: "Table Tennis" },
  { value: "ski", label: "Skiing" }, { value: "padel", label: "Padel" },
  { value: "squash", label: "Squash" }, { value: "badminton", label: "Badminton" },
  { value: "climbing", label: "Climbing" },
];

export default function ProfilePage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const showToast = useToast();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [messaging, setMessaging] = useState(false);
  const [aiExplanation, setAiExplanation] = useState("");
  const [aiError, setAiError] = useState("");
  const [loadingAI, setLoadingAI] = useState(false);
  const abortRef = useRef(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [blockLoading, setBlockLoading] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    sport: "tennis",
    proposed_time: "",
    proposed_times: ["", "", ""],
    message: "",
    venue_hint: "",
    multi_time_mode: false,
  });
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState(false);
  const inviteTrapRef = useFocusTrap(showInviteModal, () => setShowInviteModal(false));

  useEffect(() => {
    const isOwnProfile = currentUser && userId === currentUser.id;
    const fetchUrl = isOwnProfile
      ? `${API}/auth/me`
      : `${API}/users/${userId}`;
    axios.get(fetchUrl)
      .then((r) => setProfile(r.data))
      .catch(() => setProfile(null))
      .finally(() => setLoading(false));
  }, [userId, currentUser]);

  const handleMessage = async () => {
    if (!currentUser) { navigate("/login"); return; }
    setMessaging(true);
    try {
      await axios.post(`${API}/conversations`, {
        participant_id: profile.id,
        message: `Hi ${profile.name}! I found your profile and would love to connect for a game.`,
      });
      navigate("/messages");
    } catch (e) {
      console.error(e);
      setMessaging(false);
    }
  };

  const handleToggleSave = async () => {
    if (!currentUser) { navigate("/login"); return; }
    try {
      if (isSaved) {
        await axios.delete(`${API}/partners/${profile.id}/save`, { withCredentials: true });
        setIsSaved(false);
        showToast("Partner removed from saved.");
      } else {
        await axios.post(`${API}/partners/${profile.id}/save`, {}, { withCredentials: true });
        setIsSaved(true);
        showToast("Partner saved!");
      }
    } catch { showToast("Failed to update."); }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    const times = inviteForm.multi_time_mode
      ? inviteForm.proposed_times.filter((t) => t.trim())
      : [inviteForm.proposed_time].filter((t) => t.trim());
    if (times.length === 0) return;
    setInviteLoading(true);
    try {
      await axios.post(
        `${API}/sessions`,
        {
          invitee_id: profile.id,
          sport: inviteForm.sport,
          proposed_times: times,
          proposed_time: times[0] || "",
          message: inviteForm.message,
          venue_hint: inviteForm.venue_hint,
        },
        { withCredentials: true }
      );
      setInviteSuccess(true);
      setTimeout(() => { setShowInviteModal(false); setInviteSuccess(false); }, 2000);
    } catch (err) {
      console.error(err);
    } finally {
      setInviteLoading(false);
    }
  };

  const getMatchExplanation = async () => {
    if (!currentUser || !profile) return;
    setLoadingAI(true);
    setAiExplanation("");
    setAiError("");
    const sport = profile.sports?.[0]?.sport || "tennis";
    try {
      const resp = await fetch(`${API}/ai/match-explanation`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ player1: currentUser, player2: profile, sport }),
      });
      // Check the status before reading a stream. When AI is not configured the
      // backend answers 503 with a JSON body, and reading that as SSE finds no
      // `data:` lines - so the user saw a spinner stop with no message at all.
      // Error frames inside an already-open stream are handled below.
      if (!resp.ok) {
        let detail = `AI explanations are unavailable right now (HTTP ${resp.status}).`;
        try {
          const body = await resp.json();
          if (body?.detail) detail = body.detail;
        } catch {
          /* non-JSON error body; keep the status-based message */
        }
        setAiError(detail);
        return;
      }
      const reader = resp.body?.getReader();
      const decoder = new TextDecoder();
      if (!reader) return;
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (line.startsWith("data: ")) {
            try {
              const json = JSON.parse(line.slice(6));
              if (json.text) setAiExplanation((prev) => prev + json.text);
              // Shown rather than silently dropped, which is what happened.
              else if (json.error) setAiError(json.error);
            } catch {}
          }
        }
      }
    } catch (e) {
      console.error(e);
      setAiError("Couldn't load the match explanation. Please try again.");
    } finally {
      setLoadingAI(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center pt-16">
      <div className="w-8 h-8 border-2 border-brand-green border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!profile) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center pt-16 px-4">
      <div className="text-center">
        <h2 className="text-2xl font-heading font-bold text-brand-navy mb-2">Player not found</h2>
        <Link to="/find-partners" className="text-brand-green hover:underline text-sm">Back to search</Link>
      </div>
    </div>
  );

  const isOwn = currentUser?.id === profile.id;
  const initials = profile.name?.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-gray-500 hover:text-white text-sm mb-6 transition-colors">
          <ArrowLeft size={16} /> Back
        </button>

        {/* Profile header */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 mb-5" data-testid="profile-header">
          <div className="flex flex-col sm:flex-row gap-5 items-start">
            {/* Avatar */}
            <div className="relative flex-shrink-0">
              {profile.avatar_url ? (
                <img src={profile.avatar_url} alt={profile.name} className="w-24 h-24 rounded-xl object-cover" />
              ) : (
                <div className="w-24 h-24 rounded-xl bg-brand-green/10 flex items-center justify-center text-brand-green font-black font-heading text-3xl">
                  {initials}
                </div>
              )}
              {profile.subscription === "pro" && (
                <div className="absolute -top-2 -right-2 w-7 h-7 bg-brand-green rounded-full flex items-center justify-center">
                  <Zap size={13} className="text-white" fill="currentColor" />
                </div>
              )}
              {profile.subscription === "elite" && (
                <div className="absolute -top-2 -right-2 w-7 h-7 bg-yellow-400 rounded-full flex items-center justify-center">
                  <Star size={13} className="text-white" fill="currentColor" />
                </div>
              )}
            </div>

            <div className="flex-1">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <h1 className="text-2xl font-heading font-bold text-brand-navy">{profile.name}</h1>
                  <div className="flex items-center gap-1.5 text-gray-500 text-sm mt-1">
                    <MapPin size={13} /> {profile.city || "Fort Worth, TX"}
                  </div>
                  {/* Reliability badge + Verified */}
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {profile.verified_badge && <VerifiedPartnerBadge />}
                    <ReliabilityBadge
                      badge={profile.reliability_badge?.badge || (profile.completed_sessions >= 3 ? (profile.reliability_score >= 90 ? "highly_reliable" : "reliable") : "new_player")}
                      label={profile.reliability_badge?.label || (profile.completed_sessions >= 3 ? (profile.reliability_score >= 90 ? "Highly Reliable" : "Reliable") : "New Player")}
                    />
                    {(() => {
                      const ps = profile.sports?.[0]?.sport;
                      const conf = ps && profile.skill_confidence?.[ps];
                      return conf ? <SkillConfidenceBadge level={conf.level} label={conf.label} /> : null;
                    })()}
                  </div>
                </div>

                {!isOwn && (
                  <div className="flex gap-2 flex-wrap">
                    <button
                      onClick={getMatchExplanation}
                      disabled={loadingAI || !currentUser}
                      className="flex items-center gap-1.5 px-3 py-2 text-xs bg-brand-green/10 border border-brand-green/30 text-brand-green rounded-lg hover:bg-brand-green/10 transition-colors disabled:opacity-40"
                      data-testid="ai-match-button"
                    >
                      <Zap size={13} /> {loadingAI ? "Analyzing..." : "AI Match Score"}
                    </button>
                    <button
                      onClick={handleToggleSave}
                      className={`flex items-center gap-1.5 px-3 py-2 text-xs border rounded-lg transition-colors ${
                        isSaved
                          ? "bg-brand-green/10 border-brand-green text-brand-green"
                          : "bg-gray-100 border-gray-200 text-gray-600 hover:border-brand-green/40"
                      }`}
                      data-testid="save-partner-btn"
                    >
                      {isSaved ? <BookmarkCheck size={13} /> : <Bookmark size={13} />}
                      {isSaved ? "Saved" : "Save"}
                    </button>
                    <button
                      onClick={() => { if (!currentUser) { navigate("/login"); return; } setShowInviteModal(true); }}
                      className="flex items-center gap-1.5 px-3 py-2 text-xs bg-gray-100 border border-gray-200 text-gray-600 rounded-lg hover:border-brand-green/40 hover:text-white transition-colors"
                      data-testid="invite-session-button"
                    >
                      <CalendarPlus size={13} /> Invite to Session
                    </button>
                    <button
                      onClick={handleMessage}
                      disabled={messaging}
                      className="flex items-center gap-1.5 px-4 py-2 text-sm font-bold bg-brand-orange text-white rounded-lg hover:bg-brand-orange-hover transition-colors disabled:opacity-50"
                      data-testid="message-player-button"
                    >
                      <MessageSquare size={14} /> Message
                    </button>
                    {currentUser && (
                      <button
                        onClick={() => setShowReportModal(true)}
                        className="flex items-center gap-1.5 px-3 py-2 text-xs border border-red-200 text-red-400 rounded-lg hover:bg-red-50 transition-colors"
                        data-testid="report-user-button"
                        aria-label={`Report ${profile.name}`}
                      >
                        <Flag size={13} /> Report
                      </button>
                    )}
                    {currentUser && (
                      <button
                        onClick={async () => {
                          setBlockLoading(true);
                          try {
                            if (isBlocked) {
                              await axios.delete(`${API}/users/${profile.id}/block`, { withCredentials: true });
                              setIsBlocked(false);
                              showToast(`${profile.name} unblocked.`);
                            } else {
                              await axios.post(`${API}/users/${profile.id}/block`, {}, { withCredentials: true });
                              setIsBlocked(true);
                              showToast(`${profile.name} blocked. They won't appear in your searches.`);
                            }
                          } catch { showToast("Action failed. Try again."); }
                          finally { setBlockLoading(false); }
                        }}
                        disabled={blockLoading}
                        className="flex items-center gap-1.5 px-3 py-2 text-xs border border-red-400/30 text-red-400 rounded-lg hover:bg-red-400/10 transition-colors disabled:opacity-50"
                        data-testid="block-user-btn"
                        aria-label={isBlocked ? `Unblock ${profile.name}` : `Block ${profile.name}`}
                      >
                        {isBlocked ? <><ShieldOff size={13} /> Unblock</> : <><Shield size={13} /> Block</>}
                      </button>
                    )}
                  </div>
                )}
                {isOwn && (
                  <Link to="/account" className="px-4 py-2 text-sm border border-gray-200 text-gray-600 rounded-lg hover:border-brand-green/40 transition-colors" data-testid="edit-profile-button">
                    Edit Profile
                  </Link>
                )}
              </div>

              {/* Stats row */}
              <div className="flex items-center gap-5 mt-4 pt-4 border-t border-gray-200">
                <div className="text-center">
                  <div className="text-lg font-bold text-brand-navy">{profile.reliability_score || 95}%</div>
                  <div className="text-xs text-gray-400">Reliability</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-brand-navy">{profile.completed_sessions || 0}</div>
                  <div className="text-xs text-gray-400">Sessions</div>
                </div>
                <div className="text-center capitalize">
                  <div className="text-lg font-bold text-brand-navy">{profile.play_intent || "Both"}</div>
                  <div className="text-xs text-gray-400">Play Style</div>
                </div>
                <div>
                  <div className="flex flex-wrap gap-1">
                    {(profile.languages || ["English"]).map((l) => (
                      <span key={l} className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded">{l}</span>
                    ))}
                  </div>
                  <div className="text-xs text-gray-400 mt-0.5">Languages</div>
                </div>
              </div>
            </div>
          </div>

          {profile.bio && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="text-sm text-gray-600 leading-relaxed">{profile.bio}</p>
            </div>
          )}

          {/* AI Explanation */}
          {(aiExplanation || loadingAI || aiError) && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex items-center gap-2 mb-2">
                <Zap size={14} className="text-brand-green" />
                <span className="text-xs font-bold text-brand-green uppercase tracking-wider">AI Match Analysis</span>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed">
                {aiError ? (
                  // Shown rather than leaving the spinner to stop on nothing.
                  <span className="text-gray-500" role="status" data-testid="ai-error">
                    {aiError}
                  </span>
                ) : loadingAI && !aiExplanation ? (
                  <span className="text-gray-400 animate-pulse">Analyzing compatibility...</span>
                ) : (
                  aiExplanation
                )}
              </p>
            </div>
          )}
        </div>

        {/* Sports profiles */}
        {profile.sports?.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-xl p-6 mb-5" data-testid="sports-profiles">
            <h2 className="text-lg font-bold text-brand-navy mb-4 flex items-center gap-2">
              <Users size={18} className="text-brand-green" /> Sport Profiles
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {profile.sports.map((s, i) => (
                <div key={i} className="bg-gray-100 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-bold text-brand-navy capitalize">{SPORT_LABELS[s.sport] || s.sport}</h3>
                    <SkillBadge level={s.skill_level} />
                  </div>
                  {profile.skill_confidence?.[s.sport] && (
                    <div className="mb-2">
                      <SkillConfidenceBadge level={profile.skill_confidence[s.sport].level} label={profile.skill_confidence[s.sport].label} size="xs" />
                    </div>
                  )}
                  {s.rating && (
                    <div className="text-sm text-gray-500">
                      Rating: <span className="text-brand-green font-bold">{s.rating}</span>
                    </div>
                  )}
                  {s.preferred_format && (
                    <div className="text-xs text-gray-400 mt-1 capitalize">Format: {s.preferred_format}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Preferences */}
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <h2 className="text-lg font-bold text-brand-navy mb-4 flex items-center gap-2">
            <Shield size={18} className="text-brand-green" /> Play Preferences
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { label: "Equipment", value: profile.has_equipment ? "Has own gear" : "Needs gear" },
              { label: "Can Host", value: profile.can_host ? "Yes, can host" : "Not at home" },
              { label: "Intent", value: profile.play_intent || "Casual & Competitive" },
              { label: "Community", value: profile.women_only ? "Women-only events" : "Open to all" },
            ].map(({ label, value }) => (
              <div key={label}>
                <div className="text-xs text-gray-400 mb-1">{label}</div>
                <div className="text-sm text-gray-600 capitalize">{value}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Session Invite Modal */}
      {showInviteModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="invite-modal-title"
          data-testid="invite-modal"
        >
          <div
            className="bg-[#1A1F2E] border border-gray-200 rounded-xl p-6 w-full max-w-md relative dark-section"
            ref={inviteTrapRef}
          >
            <button
              onClick={() => setShowInviteModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
              aria-label="Close invite modal"
              data-testid="close-invite-modal"
            >
              <X size={18} />
            </button>
            <h3 className="text-lg font-bold text-brand-navy mb-1" id="invite-modal-title">Invite to Session</h3>
            <p className="text-sm text-gray-500 mb-5">Send a play request to <strong className="text-white">{profile.name}</strong></p>
            {inviteSuccess ? (
              <div className="text-center py-6" data-testid="invite-success">
                <div className="w-12 h-12 rounded-full bg-brand-green/10 flex items-center justify-center mx-auto mb-3">
                  <CalendarPlus size={22} className="text-brand-green" />
                </div>
                <p className="text-white font-bold">Invite sent!</p>
                <p className="text-gray-500 text-sm mt-1">{profile.name} will be notified by email.</p>
              </div>
            ) : (
              <form onSubmit={handleInvite} className="space-y-4">
                <div>
                  <label htmlFor="Sport" className="text-xs text-gray-500 uppercase tracking-wider block mb-1.5">Sport</label><select id="Sport"
                    value={inviteForm.sport}
                    onChange={(e) => setInviteForm((p) => ({ ...p, sport: e.target.value }))}
                    className="w-full bg-gray-100 border border-gray-200 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-brand-green/50"
                    data-testid="invite-sport-select"
                  >
                    {SPORTS_LIST.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>

                {/* Time mode toggle */}
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setInviteForm((p) => ({ ...p, multi_time_mode: false }))}
                    className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${!inviteForm.multi_time_mode ? "bg-brand-green/10 border-brand-green/40 text-brand-green" : "border-white/10 text-gray-500 hover:text-white"}`}
                    data-testid="single-time-mode-btn"
                  >
                    Single Time
                  </button>
                  <button
                    type="button"
                    onClick={() => setInviteForm((p) => ({ ...p, multi_time_mode: true }))}
                    className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${inviteForm.multi_time_mode ? "bg-brand-green/10 border-brand-green/40 text-brand-green" : "border-white/10 text-gray-500 hover:text-white"}`}
                    data-testid="multi-time-mode-btn"
                  >
                    Offer 2–3 Times
                  </button>
                </div>

                {!inviteForm.multi_time_mode ? (
                  <div>
                    <label htmlFor="Proposed-Time" className="text-xs text-gray-500 uppercase tracking-wider block mb-1.5">Proposed Time</label><input id="Proposed-Time"
                      type="text"
                      required
                      value={inviteForm.proposed_time}
                      onChange={(e) => setInviteForm((p) => ({ ...p, proposed_time: e.target.value }))}
                      placeholder="Saturday ~3pm, weekday evenings..."
                      className="w-full bg-gray-100 border border-gray-200 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-400 focus:outline-none focus:border-brand-green/50"
                      data-testid="invite-time-input"
                    />
                  </div>
                ) : (
                  <div className="space-y-2">
                    <label htmlFor="Proposed-Times-Up-To" className="text-xs text-gray-500 uppercase tracking-wider block">Proposed Times (up to 3)</label>
                    {[0, 1, 2].map((idx) => (
                      <input id="Proposed-Times-Up-To"
                        key={idx}
                        type="text"
                        value={inviteForm.proposed_times[idx] || ""}
                        onChange={(e) => {
                          const times = [...inviteForm.proposed_times];
                          times[idx] = e.target.value;
                          setInviteForm((p) => ({ ...p, proposed_times: times }));
                        }}
                        placeholder={`Option ${idx + 1}${idx === 0 ? " (required)" : " (optional)"}`}
                        required={idx === 0}
                        className="w-full bg-gray-100 border border-gray-200 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-400 focus:outline-none focus:border-brand-green/50"
                        data-testid={`invite-time-input-${idx}`}
                      />
                    ))}
                  </div>
                )}

                <div>
                  <label htmlFor="Message" className="text-xs text-gray-500 uppercase tracking-wider block mb-1.5">Message</label><textarea id="Message"
                    value={inviteForm.message}
                    onChange={(e) => setInviteForm((p) => ({ ...p, message: e.target.value }))}
                    rows={3}
                    placeholder="Hey! Want to hit some balls this weekend?"
                    className="w-full bg-gray-100 border border-gray-200 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-400 focus:outline-none focus:border-brand-green/50 resize-none"
                    data-testid="invite-message-input"
                  />
                </div>
                <div>
                  <label htmlFor="Venue-Optional" className="text-xs text-gray-500 uppercase tracking-wider block mb-1.5">Venue (optional)</label><input id="Venue-Optional"
                    type="text"
                    value={inviteForm.venue_hint}
                    onChange={(e) => setInviteForm((p) => ({ ...p, venue_hint: e.target.value }))}
                    placeholder="Bayard H. Friedman Tennis Center..."
                    className="w-full bg-gray-100 border border-gray-200 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-400 focus:outline-none focus:border-brand-green/50"
                    data-testid="invite-venue-input"
                  />
                </div>
                <div className="flex gap-3 pt-1">
                  <button type="button" onClick={() => setShowInviteModal(false)} className="flex-1 py-2 text-sm border border-gray-200 text-gray-500 rounded-lg hover:border-slate-500 transition-colors">
                    Cancel
                  </button>
                  <button type="submit" disabled={inviteLoading} className="flex-1 py-2 text-sm font-bold bg-brand-orange text-white rounded-lg hover:bg-brand-orange-hover transition-colors disabled:opacity-50" data-testid="send-invite-button">
                    {inviteLoading ? "Sending..." : "Send Invite"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Report issue modal */}
      {showReportModal && profile && (
        <ReportIssueModal
          reportedUser={{ id: profile.id, name: profile.name }}
          onClose={() => setShowReportModal(false)}
        />
      )}
    </div>
  );
}
